/* eslint-disable @typescript-eslint/no-explicit-any */
type Debounced<T extends (...args: any[]) => any> = {
  (...args: Parameters<T>): void;
  cancel(): void;
  flush(): ReturnType<T> | undefined;
  pending(): boolean;
};

/**
 * Simple debounce
 * @param fn function to debounce
 * @param wait milliseconds to wait
 */
export function debounce<T extends (...args: any[]) => any>(
  fn: T,
  wait: number,
): Debounced<T> {
  if (typeof fn !== "function") {
    throw new TypeError("Expected a function");
  }

  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: Parameters<T> | null = null;
  let lastThis: any = null;

  const invoke = () => {
    const args = lastArgs!;
    const thisArg = lastThis;
    lastArgs = null;
    lastThis = null;
    timer = null;
    return fn.apply(thisArg, args);
  };

  const debounced = function (this: any, ...args: Parameters<T>) {
    if (!Number.isFinite(wait) || wait <= 0) {
      fn.apply(this, args);
      return;
    }

    lastArgs = args;
    lastThis = this;

    if (timer !== null) {
      clearTimeout(timer);
    }

    timer = setTimeout(() => {
      try {
        invoke();
      } catch (err) {
        throw err;
      }
    }, wait);
  } as Debounced<T>;

  debounced.cancel = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    lastArgs = null;
    lastThis = null;
  };

  debounced.flush = () => {
    if (timer === null) return undefined;
    clearTimeout(timer);
    return invoke() as ReturnType<T>;
  };

  debounced.pending = () => timer !== null;

  return debounced;
}
